import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const C = { primary: '#8B1A1A', gold: '#D4A017' };

const STATUS_COLORS = {
  pending:    { bg: '#fff3e0', text: '#e65100' },
  confirmed:  { bg: '#e3f2fd', text: '#1565c0' },
  processing: { bg: '#e3f2fd', text: '#1565c0' },
  shipped:    { bg: '#e8f5e9', text: '#2e7d32' },
  delivered:  { bg: '#e8f5e9', text: '#1b5e20' },
  cancelled:  { bg: '#ffebee', text: '#c62828' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status?.toLowerCase()] || { bg: '#f5f5f5', text: '#666' };
  return (
    <span style={{ background: s.bg, color: s.text, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
      {status || 'Pending'}
    </span>
  );
}

export default function OrderHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get('/orders/my/')
      .then(res => setOrders(res.data.results || res.data))
      .catch(() => setError('Failed to load orders. Please try again.'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80, color: '#888', fontSize: 18 }}>Loading orders…</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <p style={{ color: '#e53935', marginBottom: 16 }}>{error}</p>
        <button onClick={() => window.location.reload()} style={{ background: C.primary, color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 4, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div style={{ minHeight: '55vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>📦</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#2C2C2C', marginBottom: 10 }}>No orders yet</h2>
        <p style={{ color: '#888', marginBottom: 28 }}>Your order history will appear here once you place an order.</p>
        <Link to="/shop" style={{ background: C.primary, color: '#fff', textDecoration: 'none', padding: '12px 28px', borderRadius: 4, fontSize: 15, fontWeight: 600 }}>
          Start Shopping
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: '#2C2C2C', marginBottom: 8 }}>My Orders</h1>
      <p style={{ color: '#888', marginBottom: 32, fontSize: 14 }}>{orders.length} order{orders.length !== 1 ? 's' : ''}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {orders.map(order => (
          <div key={order.id} style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            <div style={{ background: '#FFF8F0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, borderBottom: '1px solid #f0e0e0' }}>
              <div>
                <span style={{ fontSize: 13, color: '#888' }}>Order #</span>
                <span style={{ fontWeight: 700, color: '#2C2C2C' }}>{order.id}</span>
              </div>
              {order.created_at && (
                <span style={{ fontSize: 13, color: '#888' }}>
                  {new Date(order.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
              )}
              <StatusBadge status={order.status} />
              <span style={{ fontWeight: 700, color: C.primary, fontSize: 16 }}>
                ₹{parseFloat(order.total || order.total_amount || 0).toLocaleString('en-IN')}
              </span>
            </div>

            <div style={{ padding: '16px 20px' }}>
              {order.shipping_address && (
                <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                  📍 {order.shipping_address.full_name || order.full_name}, {order.shipping_address.city || order.city}
                </p>
              )}
              {!order.shipping_address && order.full_name && (
                <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                  📍 {order.full_name}{order.city ? `, ${order.city}` : ''}
                </p>
              )}

              {(order.items || order.order_items || []).length > 0 ? (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(order.items || order.order_items).map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF8F0', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
                      {item.product_image && (
                        <img src={item.product_image} alt="" style={{ width: 36, height: 44, objectFit: 'cover', borderRadius: 4 }} />
                      )}
                      <div>
                        <p style={{ fontWeight: 600, color: '#2C2C2C' }}>{item.product_name || item.product}</p>
                        <p style={{ color: '#888' }}>
                          Qty: {item.quantity}{item.size ? ` · Size: ${item.size}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#aaa', fontSize: 13 }}>Order details loading…</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
